import { useState } from 'react';

import type { ApiS3File } from 'api/types/ApiS3File';
import type { ApiS3FileId } from 'api/types/ApiTypedId';
import { useI18n } from 'i18n/hooks/useI18n';
import { HttpError } from 'utils/HttpError';

import { useKeyedState } from './useKeyedState';

export type FileUploadStateAndActions = ReturnType<
  typeof useFileUpload
>['fileUploadStateAndActions'];

type Args = {
  fileId: ApiS3FileId | null;
  fileMeta: ApiS3File | null;
  onChange: (newFileId: ApiS3FileId | null) => void;
  onError: (error: Error) => void;
  maxSizeInMB: number | null;
  canRemove: boolean;
  uploadFileToS3: (file: File) => Promise<ApiS3FileId>;
};

export function useFileUpload({
  fileId,
  fileMeta,
  onChange,
  onError,
  maxSizeInMB,
  canRemove,
  uploadFileToS3,
}: Args) {
  const { i18n } = useI18n();

  // Instead of setting setIsFileUploading to false after uploading
  // just wait for the fileId to be set
  //
  // This avoid the UI flickering while state syncs
  const [isFileUploading, setIsFileUploading] = useKeyedState(false, {
    key: fileId ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const onFileSelected = async (newFile: File) => {
    if (maxSizeInMB && newFile.size > maxSizeInMB * 1000000) {
      setError(
        i18n('Maximum file size: %(maxSize)s MB', { maxSize: maxSizeInMB }),
      );
      return;
    }

    setIsFileUploading(true);

    try {
      const newFileId = await uploadFileToS3(newFile);
      onChange(newFileId);
    } catch (error) {
      if (
        error instanceof HttpError &&
        error.responseJson?.errors.some(
          (err) => err.name === 'mimeType' && err.description === 'Required',
        )
      ) {
        setError(i18n('Unsupported file format'));
      } else {
        onError(error as Error);
      }

      setIsFileUploading(false);
    }
  };

  const onFileRemoved = canRemove
    ? () => {
        onChange(null);
        return;
      }
    : null;

  const fileUploadStateAndActions = {
    fileId,
    fileMeta,
    loading: Boolean(isFileUploading || (fileId && !fileMeta)),
    onFileSelected,
    onFileRemoved,
    error,
  };

  return { fileUploadStateAndActions };
}
